import React, { useEffect, useState } from 'react';
import { useWikiV2Client, wikiErrorCode } from '../../../hooks/useWikiV2';
import WikiModalShell, { WikiCreateButton, WikiModalError } from './WikiModalShell';
import { WIKI_MANAGE_COPY } from './wikiModalCopy';
import { wikiModalErrorText, wikiPlanRefusal } from './wikiModalErrors';

/**
 * A confirmation in the Wiki modal frame (#872): archive a category or a page.
 * Opened with `wikiUi.openModal('confirm', opts)`:
 *
 *   title         dialog title
 *   body          the text, or
 *   loadBody      `(client) => Promise<string>` (e.g. the archive preview's
 *                 counts); the action stays disabled until it resolves, and a
 *                 failure (MFA, gone) is shown instead
 *   confirmLabel  the action button, `icon`, `danger` (red), `action`
 *   requires      'edit' | 'admin' — WikiModalHost closes it for anyone else
 *   run           `(client) => Promise` — the request
 *   onDone        `(result) => void` after the modal closed (navigate, toast,
 *                 refresh the cache)
 *   errorCopy     optional `{ code: message }`, checked before the shared copy
 *                 (that copy is written for pickers: "Choose another one")
 *
 * Built on WikiModalShell rather than the app's Radix AlertDialog, which sits
 * at z-50, under the Wiki modals (z 91+). Errors stay inline and the modal
 * stays open, so nothing fails silently.
 */
export default function WikiConfirmModal({ opts = {}, onClose }) {
    const client = useWikiV2Client();
    const [body, setBody] = useState(() => (opts.loadBody ? null : (opts.body || '')));
    const [loadError, setLoadError] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const errorText = (err) => {
        const own = opts.errorCopy && opts.errorCopy[wikiErrorCode(err)];
        if (own) return own;
        const refusal = wikiPlanRefusal(err);
        return refusal ? refusal.message : wikiModalErrorText(err);
    };

    useEffect(() => {
        if (!opts.loadBody) return undefined;
        let live = true;
        Promise.resolve()
            .then(() => opts.loadBody(client))
            .then((text) => { if (live) setBody(text || ''); })
            .catch((err) => { if (live) setLoadError(errorText(err)); });
        return () => { live = false; };
        // Loads once per open: the modal is keyed on each openModal() call.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ready = body !== null && !loadError;

    const confirm = async () => {
        if (!ready || busy || !opts.run) return;
        setBusy(true);
        setError('');
        let result;
        try {
            result = await opts.run(client);
        } catch (err) {
            setBusy(false);
            setError(errorText(err));
            return;
        }
        onClose();
        // The action succeeded; a failure in the follow-up (navigate, refresh)
        // must not be reported as if it hadn't. Log it instead.
        if (opts.onDone) {
            try {
                await opts.onDone(result);
            } catch (err) {
                console.error('[wiki] after-archive step failed', err);
            }
        }
    };

    return (
        <WikiModalShell
            title={opts.title || ''}
            onClose={onClose}
            testId={opts.testId || 'wiki-confirm-modal'}
            footer={(
                <WikiCreateButton
                    enabled={ready}
                    busy={busy}
                    onClick={confirm}
                    icon={opts.icon}
                    iconSize={15}
                    variant={opts.danger ? 'danger' : 'primary'}
                    action={opts.action}
                    testId="wiki-confirm-submit"
                >
                    {opts.confirmLabel || WIKI_MANAGE_COPY.confirm}
                </WikiCreateButton>
            )}
        >
            {!loadError && (
                <p className="wk-t-dense wk-confirm-body" data-testid="wiki-confirm-body" aria-live="polite">
                    {body === null ? WIKI_MANAGE_COPY.counting : body}
                </p>
            )}
            <WikiModalError message={error || loadError} />
        </WikiModalShell>
    );
}
